<template>
  <div
    ref="sliderContainer"
    class="relative select-none"
    :class="expanded && dialEnabled ? 'z-10' : ''">
    <!-- Header: label + score area -->
    <div class="flex justify-between items-center">
      <!-- Label -->
      <label
        class="text-sm font-medium text-gray-300 dark:text-white transition-colors"
        :class="expanded && dialEnabled ? 'text-white' : ''">
        {{ label }}
      </label>

      <!-- Score + Info area: tap to expand, vertical drag to adjust -->
      <div
        ref="scoreAreaRef"
        class="flex items-center gap-2 px-2 py-1 -mr-2 rounded-lg transition-colors cursor-pointer"
        :class="expanded && dialEnabled ? 'bg-white/10' : 'active:bg-white/5'"
        style="touch-action: none"
        @touchmove.stop.prevent
        @pointerdown.stop="onScorePointerDown"
        @pointermove.stop="onScorePointerMove"
        @pointerup.stop="onScorePointerUp"
        @pointercancel.stop="onScorePointerUp">
        <fa-icon
          icon="circle-info"
          class="text-xs transition-colors"
          :class="
            expanded && dialEnabled ? 'text-gray-300' : 'text-gray-500'
          " />
        <span
          class="text-sm font-medium tabular-nums select-none transition-colors"
          :class="
            expanded && dialEnabled
              ? 'text-white'
              : 'text-gray-300 dark:text-white'
          ">
          {{ displayValue }}
        </span>
      </div>
    </div>

    <!-- Slider track area (touch target) -->
    <div
      class="mb-2 cursor-pointer"
      :class="expanded && dialEnabled ? 'py-4' : 'py-3'"
      style="touch-action: none"
      @touchmove.stop.prevent
      @pointerdown="onTrackPointerDown"
      @pointermove="onTrackPointerMove"
      @pointerup="onTrackPointerUp"
      @pointercancel="onTrackPointerUp">
      <div
        ref="trackRef"
        class="relative rounded-sm bg-gray-700 transition-all duration-200"
        :class="expanded && dialEnabled ? 'h-3' : 'h-1.5'">
        <!-- Filled portion -->
        <div
          class="absolute -top-2 left-0 py-3 rounded-l-sm pointer-events-none transition-colors"
          :style="{ width: thumbPercent + '%', backgroundColor: hslColor }" />

        <!-- Thumb marker — matches full height of the colored bar -->
        <div
          class="absolute -top-2 py-3 w-4 pointer-events-none rounded-r-sm shadow-lg"
          :style="{
            left:
              'calc(' +
              thumbPercent +
              '% - ' +
              (thumbPercent / 100) * 16 +
              'px)',
            backgroundColor: thumbColor
          }" />

        <!-- Tick marks (expanded only) -->
        <template v-if="expanded && dialEnabled">
          <div
            v-for="tick in tickMarks"
            :key="tick.value"
            class="absolute pointer-events-none"
            :style="{ left: (tick.value / 10) * 100 + '%', top: '100%' }">
            <div
              class="w-px"
              :class="tick.major ? 'h-3 bg-gray-400' : 'h-2 bg-gray-600'"
              style="transform: translateX(-0.5px); margin-top: 4px" />
            <span
              v-if="tick.major"
              class="block text-[9px] text-gray-500 text-center mt-0.5"
              style="
                transform: translateX(-50%);
                width: 16px;
                margin-left: -7.5px;
              ">
              {{ tick.value }}
            </span>
          </div>
        </template>
      </div>
    </div>

    <!-- Info popup (when dial is disabled) -->
    <p
      v-if="showInfo && !dialEnabled"
      class="text-xs text-gray-400 -mt-1 mb-2">
      {{ infoDescription }}
    </p>

    <!-- Expanded content: info description + dial -->
    <div
      v-if="expanded && dialEnabled"
      class="overflow-hidden transition-all duration-200"
      :style="{
        maxHeight: expanded ? '320px' : '0',
        opacity: expanded ? 1 : 0
      }">
      <!-- Info description -->
      <p class="text-xs text-gray-400 mb-3 mt-2">{{ infoDescription }}</p>

      <!-- Dial control -->
      <FormSliderDial
        :value="Number(localModel)"
        :color="hslColor"
        :label="label"
        @update:value="updateModel" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useReviewColors } from '@/use/useReviewColors'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { Capacitor } from '@capacitor/core'
import confetti from 'canvas-confetti'
import FormSliderDial from './FormSliderDial.vue'

const { score, hslColor } = useReviewColors()
const props = defineProps({
  label: String,
  model: Number,
  expanded: { type: Boolean, default: false },
  dialEnabled: { type: Boolean, default: false }
})

const emit = defineEmits([
  'update:model',
  'maxed',
  'zeroed',
  'request-expand',
  'request-collapse'
])

const localModel = ref(props.model)
const showInfo = ref(false)
const sliderContainer = ref(null)
const trackRef = ref(null)
const scoreAreaRef = ref(null)
const hasTriggeredMax = ref(false)
const hasTriggeredMin = ref(false)

// Multi-touch: track which pointerId is active on the track
const activeTrackPointerId = ref(null)

const displayValue = computed(() => {
  const v = Number(localModel.value)
  return v === Math.floor(v) ? v.toFixed(1) : v.toFixed(1)
})

// Thumb position as percentage (0-100)
const thumbPercent = computed(() => {
  return (Number(localModel.value) / 10) * 100
})

// Brighter/more saturated variant of the score color for the thumb
const thumbColor = computed(() => {
  const base = hslColor.value
  const match = base.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)
  if (!match) return base
  const h = match[1]
  const s = Math.min(100, parseInt(match[2]) + 10)
  const l = Math.min(90, parseInt(match[3]) + 25)
  return `hsl(${h}, ${s}%, ${l}%)`
})

// Tick marks for expanded mode
const tickMarks = computed(() => {
  const result = []
  for (let i = 0; i <= 20; i++) {
    const value = i * 0.5
    result.push({ value, major: value === Math.floor(value) })
  }
  return result
})

// Info descriptions per label
const infoDescription = computed(() => {
  const descriptions = {
    Story:
      'Narrative structure and depth. Low end: Predictable, Shallow. High end: Intriguing, Complex.',
    Characters:
      'Character development and portrayal. Low end: Flat, Stereotypical. High end: Multidimensional, Compelling.',
    Execution:
      'Technical quality and direction. Low end: Sloppy, Unpolished. High end: Masterful, Sleek.',
    Entertainment:
      'Enjoyment and fun factor. Low end: Dull, Tedious. High end: Engaging, Thrilling.',
    Impact:
      'Emotional and societal resonance. Low end: Superficial, Forgettable. High end: Profound, Memorable.',
    Universe:
      'Setting and universe creation. Low end: Inconsistent, Sparse. High end: Rich, Believable.'
  }
  return descriptions[props.label] || ''
})

// --- Track pointer events (horizontal slider) ---
const onTrackPointerDown = (e) => {
  if (activeTrackPointerId.value !== null) return
  e.preventDefault()
  activeTrackPointerId.value = e.pointerId
  e.currentTarget.setPointerCapture(e.pointerId)
  showInfo.value = false
  // Collapse any expanded slider when interacting with a track
  emit('request-collapse')
  updateValueFromPointer(e)
}

const onTrackPointerMove = (e) => {
  if (e.pointerId !== activeTrackPointerId.value) return
  e.preventDefault()
  updateValueFromPointer(e)
}

const onTrackPointerUp = (e) => {
  if (e.pointerId !== activeTrackPointerId.value) return
  activeTrackPointerId.value = null
}

const updateValueFromPointer = (e) => {
  const track = trackRef.value
  if (!track) return
  const rect = track.getBoundingClientRect()
  const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
  const ratio = x / rect.width
  const rawValue = ratio * 10
  const snapped = Math.round(rawValue * 10) / 10
  updateModel(snapped)
}

// --- Score area pointer events (vertical drag + tap to expand) ---
let scorePointerState = null

const onScorePointerDown = (e) => {
  e.preventDefault()
  scorePointerState = {
    startY: e.clientY,
    startValue: Number(localModel.value),
    pointerId: e.pointerId,
    isDrag: false
  }
  e.currentTarget.setPointerCapture(e.pointerId)
}

const onScorePointerMove = (e) => {
  if (!scorePointerState || e.pointerId !== scorePointerState.pointerId) return
  e.preventDefault()

  const totalDy = scorePointerState.startY - e.clientY
  if (!scorePointerState.isDrag && Math.abs(totalDy) > 5) {
    scorePointerState.isDrag = true
  }

  if (scorePointerState.isDrag) {
    // 200px of vertical movement = full 0-10 range
    const sensitivity = 10 / 200
    const delta = totalDy * sensitivity
    const newValue = Math.max(
      0,
      Math.min(10, Math.round((scorePointerState.startValue + delta) * 10) / 10)
    )
    updateModel(newValue)
  }
}

const onScorePointerUp = (e) => {
  if (!scorePointerState || e.pointerId !== scorePointerState.pointerId) return

  if (!scorePointerState.isDrag) {
    if (props.dialEnabled) {
      // Tap — toggle expanded (only when dial is enabled)
      if (props.expanded) {
        emit('request-collapse')
      } else {
        emit('request-expand')
      }
    } else {
      // Tap without dial — toggle info popup
      showInfo.value = !showInfo.value
    }
  }

  scorePointerState = null
}

// --- Confetti effects ---
const sadEmojis = ['😢', '😭', '💔', '😿']
const emojiShapes = sadEmojis.map((emoji) =>
  confetti.shapeFromText({ text: emoji })
)

const triggerConfetti = () => {
  if (!sliderContainer.value) return
  const rect = sliderContainer.value.getBoundingClientRect()
  const x = (rect.left + rect.width * 0.8) / window.innerWidth
  const y = (rect.top + rect.height / 2) / window.innerHeight
  confetti({
    particleCount: 50,
    spread: 60,
    origin: { x, y },
    colors: ['#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7'],
    scalar: 0.8,
    gravity: 1.2,
    drift: 0,
    ticks: 150,
    disableForReducedMotion: true
  })
}

const triggerSadEmojis = () => {
  if (!sliderContainer.value) return
  const rect = sliderContainer.value.getBoundingClientRect()
  const x = (rect.left + rect.width * 0.1) / window.innerWidth
  const y = (rect.top + rect.height / 2) / window.innerHeight
  confetti({
    particleCount: 15,
    spread: 50,
    origin: { x, y },
    shapes: emojiShapes,
    scalar: 1.5,
    gravity: 0.8,
    drift: -0.5,
    ticks: 120,
    startVelocity: 20,
    disableForReducedMotion: true
  })
}

// --- Core update logic ---
onMounted(() => {
  score.value = props.model
})

watch(
  () => props.model,
  (newVal) => {
    localModel.value = newVal
    score.value = newVal
  }
)

const updateModel = (value) => {
  const numValue = Number(value)
  const prevValue = Number(localModel.value)

  emit('update:model', numValue)
  localModel.value = value
  score.value = numValue

  if (numValue === 10 && prevValue < 10 && !hasTriggeredMax.value) {
    hasTriggeredMax.value = true
    triggerConfetti()
    emit('maxed')
  } else if (numValue < 10) {
    hasTriggeredMax.value = false
  }

  if (numValue === 0 && prevValue > 0 && !hasTriggeredMin.value) {
    hasTriggeredMin.value = true
    triggerSadEmojis()
    emit('zeroed')
  } else if (numValue > 0) {
    hasTriggeredMin.value = false
  }

  triggerHapticFeedback()
}

const triggerHapticFeedback = () => {
  if (Capacitor.getPlatform() === 'ios') {
    Haptics.impact({ style: ImpactStyle.Light })
  }
}
</script>
